import { Component } from "react";
import PropTypes from "prop-types";
import { fetchApi, saveApi } from "../../utils/DatabaseHelper";
import { toast } from "react-toastify";
import logger from "../../logging/logger";
import LoadingSpinner from "../../utils/Loading";
import ConfirmationDialog from "../ConfirmationDialog";
import {
    Button,
    Input,
    FormGroup,
    Label,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";

// Muss exakt der Backend-Transliteration entsprechen (metaSettings.controller.js)
function generateKey(label) {
    return String(label || "")
        .toLowerCase()
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 64)
        .replace(/_+$/, "");
}

// putApi wirft res.error (hier undefined) und deleteApi prüft res.ok nicht —
// deshalb eigener Helper für PUT/DELETE.
async function api(method, url, body) {
    const res = await fetch(url, {
        method,
        headers: {
            "Content-Type": "application/json",
            Authorization: global.auth.authToken.token,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data.message || `HTTP ${res.status}`);
    }
    return data;
}

class MetaFieldsSettings extends Component {
    constructor(props) {
        super(props);
        this.state = {
            fields: [],
            loading: true,
            form: { label: "", key: "", canMail: false, showInList: false },
            editing: null,
            keyTouched: false,
            formOpen: false,
            saving: false,
            deleteRow: null,
        };
        this.handleFormChange = this.handleFormChange.bind(this);
    }

    isSuperAdmin() {
        return global.auth?.user?.rolle === "superadmin";
    }

    async load() {
        this.setState({ loading: true });
        try {
            const isSuperAdmin = this.isSuperAdmin();
            let data;
            if (isSuperAdmin && this.props.firmenId !== null) {
                data = await fetchApi(
                    "metaSettings",
                    "?firmenId=" + this.props.firmenId,
                );
            } else {
                data = await fetchApi("metaSettings");
                if (isSuperAdmin) {
                    // Backend lehnt firmenId=null ab — global clientseitig filtern
                    data = {
                        ...data,
                        data: (data.data || []).filter(
                            (f) => f.firmenId === null,
                        ),
                    };
                }
            }
            this.setState({ fields: data.data || [], loading: false });
        } catch (err) {
            logger.error("Metafelder konnten nicht geladen werden", err);
            toast.error("Metafelder konnten nicht geladen werden.");
            this.setState({ loading: false });
        }
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.firmenId !== this.props.firmenId) {
            this.load();
        }
    }

    handleFormChange(e) {
        const { id, type, checked, value } = e.target;
        const form = {
            ...this.state.form,
            [id]: type === "checkbox" ? checked : value,
        };
        const nextState = { form };
        if (id === "label" && !this.state.keyTouched) {
            form.key = generateKey(value);
        }
        if (id === "key") {
            nextState.keyTouched = true;
        }
        this.setState(nextState);
    }

    resetForm() {
        this.setState({
            form: { label: "", key: "", canMail: false, showInList: false },
            editing: null,
            keyTouched: false,
        });
    }

    openCreate() {
        this.resetForm();
        this.setState({ formOpen: true });
    }

    closeForm() {
        this.resetForm();
        this.setState({ formOpen: false });
    }

    startEdit(field) {
        this.setState({
            editing: field,
            form: {
                label: field.label,
                key: field.key,
                canMail: field.canMail === true,
                showInList: field.showInList === true,
            },
            keyTouched: true,
            formOpen: true,
        });
    }

    async handleSubmit(e) {
        e.preventDefault();
        if (this.state.saving) return;
        const { form, editing } = this.state;
        if (!form.label.trim()) {
            toast.error("Bitte ein Label angeben.");
            return;
        }
        if (!form.key.trim()) {
            toast.error("Bitte einen Key angeben.");
            return;
        }
        try {
            this.setState({ saving: true });
            if (editing) {
                await api(
                    "PUT",
                    global.api_url + "metaSettings/" + editing.id,
                    {
                        label: form.label,
                        key: form.key,
                        canMail: form.canMail,
                        showInList: form.showInList,
                    },
                );
                toast.success("Metafeld gespeichert.");
            } else {
                await saveApi("metaSettings", {
                    entity: "mitarbeiter",
                    label: form.label,
                    key: form.key,
                    canMail: form.canMail,
                    showInList: form.showInList,
                    ...(this.isSuperAdmin()
                        ? { firmenId: this.props.firmenId }
                        : {}),
                });
                toast.success("Metafeld angelegt.");
            }
            this.resetForm();
            this.setState({ formOpen: false });
            await this.load();
        } catch (err) {
            logger.error("Metafeld speichern fehlgeschlagen", err);
            toast.error(err.message || "Speichern fehlgeschlagen.");
        } finally {
            this.setState({ saving: false });
        }
    }

    async handleToggle(field, prop) {
        const next = !field[prop];
        this.setState((state) => ({
            fields: state.fields.map((f) =>
                f.id === field.id ? { ...f, [prop]: next } : f,
            ),
        }));
        try {
            await api("PUT", global.api_url + "metaSettings/" + field.id, {
                [prop]: next,
            });
            await this.load();
        } catch (err) {
            logger.error("Metafeld aktualisieren fehlgeschlagen", err);
            toast.error(err.message || "Speichern fehlgeschlagen.");
            this.setState((state) => ({
                fields: state.fields.map((f) =>
                    f.id === field.id ? { ...f, [prop]: field[prop] } : f,
                ),
            }));
        }
    }

    toggleDeleteDialog(field) {
        this.setState({ deleteRow: field });
    }

    async deleteField() {
        const { deleteRow } = this.state;
        this.setState({ deleteRow: null });
        try {
            await api(
                "DELETE",
                global.api_url + "metaSettings/" + deleteRow.id,
            );
            toast.success("Metafeld gelöscht.");
            await this.load();
        } catch (err) {
            logger.error("Metafeld löschen fehlgeschlagen", err);
            toast.error(err.message || "Löschen fehlgeschlagen.");
        }
    }

    render() {
        const { fields, loading, form, editing, deleteRow } = this.state;
        const scopeLabel = this.props.firmaLabel || "Global";
        let submitLabel = "Anlegen";
        if (editing) submitLabel = "Speichern";
        if (this.state.saving) submitLabel = "Speichert…";
        return (
            <div className="meta-fields-settings">
                <div className="heading row mb-3">
                    <h5 className="col">Metafelder f&uuml;r {scopeLabel}</h5>
                    <div className="col-auto">
                        <Button
                            color="primary"
                            className="btn-add-meta"
                            onClick={() => this.openCreate()}
                        >
                            <i className="fas fa-plus"></i> Neues Feld
                        </Button>
                    </div>
                </div>
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <LoadingSpinner show={loading} />
                        ) : fields.length === 0 ? (
                            <p>Keine Metafelder vorhanden.</p>
                        ) : (
                            <Table striped responsive>
                                <thead>
                                    <tr>
                                        <th>Label</th>
                                        <th>Platzhalter</th>
                                        <th>E-Mail</th>
                                        <th>Liste</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fields.map((field) => (
                                        <tr key={field.id}>
                                            <td>{field.label}</td>
                                            <td>
                                                <span className="badge badge-secondary">{`{meta_${field.key}}`}</span>
                                            </td>
                                            <td>
                                                <Input
                                                    type="checkbox"
                                                    checked={
                                                        field.canMail === true
                                                    }
                                                    onChange={() =>
                                                        this.handleToggle(
                                                            field,
                                                            "canMail",
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <Input
                                                    type="checkbox"
                                                    checked={
                                                        field.showInList ===
                                                        true
                                                    }
                                                    onChange={() =>
                                                        this.handleToggle(
                                                            field,
                                                            "showInList",
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td className="text-right">
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    className="btn-edit-meta"
                                                    onClick={() =>
                                                        this.startEdit(field)
                                                    }
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </Button>{" "}
                                                <Button
                                                    color="danger"
                                                    size="sm"
                                                    className="btn-delete-meta"
                                                    onClick={() =>
                                                        this.toggleDeleteDialog(
                                                            field,
                                                        )
                                                    }
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </div>
                </div>
                <Modal
                    className="meta-form-modal"
                    isOpen={this.state.formOpen}
                    toggle={() => this.closeForm()}
                    backdrop={true}
                    keyboard={true}
                    centered={true}
                    autoFocus={false}
                >
                    <ModalHeader toggle={() => this.closeForm()}>
                        {editing ? "Metafeld bearbeiten" : "Neues Metafeld"}
                    </ModalHeader>
                    <form onSubmit={(e) => this.handleSubmit(e)}>
                        <ModalBody>
                            <div className="row">
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label for="label">Label</Label>
                                        <Input
                                            id="label"
                                            type="text"
                                            value={form.label}
                                            onChange={this.handleFormChange}
                                            autoFocus
                                            required
                                        />
                                    </FormGroup>
                                </div>
                                <div className="col-md-6">
                                    <FormGroup>
                                        <Label for="key">Key</Label>
                                        <Input
                                            id="key"
                                            type="text"
                                            value={form.key}
                                            onChange={this.handleFormChange}
                                            required
                                        />
                                    </FormGroup>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <FormGroup check>
                                        <Label check>
                                            <Input
                                                id="canMail"
                                                type="checkbox"
                                                checked={form.canMail}
                                                onChange={this.handleFormChange}
                                            />{" "}
                                            In E-Mails verwendbar
                                        </Label>
                                    </FormGroup>
                                    <FormGroup check>
                                        <Label check>
                                            <Input
                                                id="showInList"
                                                type="checkbox"
                                                checked={form.showInList}
                                                onChange={this.handleFormChange}
                                            />{" "}
                                            In Listen anzeigen
                                        </Label>
                                    </FormGroup>
                                </div>
                            </div>
                            {editing && form.key !== editing.key && (
                                <div className="alert alert-warning">
                                    Bestehende{" "}
                                    <code>{`{meta_${editing.key}}`}</code>
                                    -Platzhalter in E-Mail-Vorlagen l&ouml;sen
                                    danach zu &bdquo;keine Info&ldquo; auf.
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="secondary"
                                type="button"
                                onClick={() => this.closeForm()}
                            >
                                Abbrechen
                            </Button>
                            <Button
                                color="primary"
                                type="submit"
                                disabled={this.state.saving}
                            >
                                {submitLabel}
                            </Button>
                        </ModalFooter>
                    </form>
                </Modal>
                <ConfirmationDialog
                    isOpen={deleteRow !== null}
                    title="Bestätigung erforderlich"
                    message={`${deleteRow ? deleteRow.valueCount : 0} gespeicherte Werte werden gelöscht.`}
                    onConfirm={() => this.deleteField()}
                    onClose={() => this.setState({ deleteRow: null })}
                />
            </div>
        );
    }
}

export default MetaFieldsSettings;

MetaFieldsSettings.propTypes = {
    firmenId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    firmaLabel: PropTypes.string,
};
